import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Download, Trash2, Globe, Lock, Copy } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useAdminApi, errMsg } from "@/lib/api";
import { fmtSize } from "@/lib/site";
import { UploadDocDialog } from "@/admin/UploadDocDialog";
import { ShareLinkDialog, shareUrl, copy } from "@/admin/ShareLinkDialog";

const linkState = (l) => (l.revoked ? "Revoked" : new Date(l.expires_at) < new Date() ? "Expired" : "Active");

const DocsList = ({ docs, call, refresh }) => {
  const preview = async (d) => {
    try {
      const blob = await call({ url: `/admin/documents/${d.id}/download`, responseType: "blob" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error("Could not open file"); }
  };
  const remove = async (d) => {
    if (!window.confirm(`Delete "${d.title}"? Its links stop working.`)) return;
    try { await call({ method: "delete", url: `/admin/documents/${d.id}` }); toast.success("Deleted"); refresh(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!docs.length) return <p className="p-6 text-sm text-stone-500" data-testid="docs-empty">No documents yet. Upload the APEDA certificate as Public first.</p>;
  return (
    <ul className="divide-y divide-stone-200" data-testid="docs-list">
      {docs.map((d) => (
        <li key={d.id} className="p-5 flex flex-wrap items-center justify-between gap-4" data-testid={`doc-row-${d.id}`}>
          <div className="flex items-start gap-3">
            {d.visibility === "public" ? <Globe className="h-4 w-4 mt-1 text-emerald-700" /> : <Lock className="h-4 w-4 mt-1 text-stone-500" />}
            <div><p className="font-medium">{d.title}</p>
              <p className="text-xs text-stone-500">{d.doc_type} · {d.visibility} · {fmtSize(d.size)}</p></div>
          </div>
          <div className="flex gap-2">
            {d.visibility === "private" && <ShareLinkDialog doc={d} onDone={refresh} />}
            <button onClick={() => preview(d)} className="rounded-full border border-stone-300 p-2 hover:bg-stone-100" data-testid={`doc-open-btn-${d.id}`}><Download className="h-3.5 w-3.5" /></button>
            <button onClick={() => remove(d)} className="rounded-full border border-stone-300 p-2 hover:bg-red-50" data-testid={`doc-delete-btn-${d.id}`}><Trash2 className="h-3.5 w-3.5 text-red-600" /></button>
          </div>
        </li>
      ))}
    </ul>
  );
};

const LinksTable = ({ links, docs, call, refresh }) => {
  const revoke = async (t) => {
    try { await call({ method: "post", url: `/admin/links/${t}/revoke` }); toast.success("Link revoked"); refresh(); } catch (e) { toast.error(errMsg(e)); }
  };
  const title = (id) => docs.find((d) => d.id === id)?.title || "(deleted)";
  return (
    <Table data-testid="links-table">
      <TableHeader><TableRow><TableHead>Document</TableHead><TableHead>For</TableHead><TableHead>Expires</TableHead><TableHead>Downloads</TableHead><TableHead>State</TableHead><TableHead /></TableRow></TableHeader>
      <TableBody>
        {!links.length && <TableRow><TableCell colSpan={6} className="text-stone-500">No links yet.</TableCell></TableRow>}
        {links.map((l) => (
          <TableRow key={l.token} data-testid={`link-row-${l.token}`}>
            <TableCell>{title(l.document_id)}</TableCell><TableCell>{l.recipient}</TableCell>
            <TableCell className="text-xs">{new Date(l.expires_at).toLocaleDateString()}</TableCell>
            <TableCell>{l.download_count}</TableCell><TableCell className="text-xs">{linkState(l)}</TableCell>
            <TableCell className="whitespace-nowrap">
              {linkState(l) === "Active" && (<>
                <button onClick={() => copy(shareUrl(l.token))} className="p-1.5" data-testid={`link-copy-btn-${l.token}`}><Copy className="h-3.5 w-3.5" /></button>
                <button onClick={() => revoke(l.token)} className="text-xs underline text-red-700 ml-2" data-testid={`link-revoke-btn-${l.token}`}>Revoke</button>
              </>)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

const Logs = ({ logs }) => (
  <Table data-testid="download-logs-table">
    <TableHeader><TableRow><TableHead>When</TableHead><TableHead>Document</TableHead><TableHead>Via</TableHead><TableHead>IP</TableHead></TableRow></TableHeader>
    <TableBody>
      {!logs.length && <TableRow><TableCell colSpan={4} className="text-stone-500">No downloads yet.</TableCell></TableRow>}
      {logs.map((g) => (
        <TableRow key={g.id}><TableCell className="text-xs">{new Date(g.at).toLocaleString()}</TableCell>
          <TableCell>{g.document_title}</TableCell><TableCell className="text-xs">{g.via}</TableCell><TableCell className="text-xs">{g.ip}</TableCell></TableRow>
      ))}
    </TableBody>
  </Table>
);

const Card = ({ title, children }) => (
  <section className="space-y-3"><h2 className="text-lg font-semibold">{title}</h2>
    <div className="bg-white border border-stone-200 rounded-md overflow-x-auto">{children}</div></section>
);

export default function AdminDocuments() {
  const call = useAdminApi();
  const qc = useQueryClient();
  const docs = useQuery({ queryKey: ["admin-docs"], queryFn: () => call({ url: "/admin/documents" }) });
  const links = useQuery({ queryKey: ["admin-links"], queryFn: () => call({ url: "/admin/links" }) });
  const logs = useQuery({ queryKey: ["admin-logs"], queryFn: () => call({ url: "/admin/download-logs" }) });
  const refresh = () => ["admin-docs", "admin-links", "admin-logs"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  return (
    <div className="space-y-10 max-w-5xl">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div><h1 className="text-3xl font-semibold tracking-tight">Documents</h1>
          <p className="text-sm text-stone-500">Public files appear on the Quality page. Private files are only reachable by a 7-day link.</p></div>
        <UploadDocDialog onDone={refresh} />
      </div>
      <Card title="Files"><DocsList docs={docs.data || []} call={call} refresh={refresh} /></Card>
      <Card title="Share links"><LinksTable links={links.data || []} docs={docs.data || []} call={call} refresh={refresh} /></Card>
      <Card title="Download log"><Logs logs={logs.data || []} /></Card>
    </div>
  );
}
